/**
 * Hybrid AHU tunnel addressing.
 *
 * Non-hybrid AHUs address a tunnel by `row:level` (position is always 1 and
 * stays hidden). Hybrid AHUs add the 3rd field (`position`, the back-to-back
 * axis) so two physically distinct tunnels sharing a (row, level) cell get
 * distinct labels.
 *
 * This is the single canonical formatter — every place that renders a tunnel
 * address (AHU tab tunnel list, tunnel-index pill strip, sizer popup, lifting
 * lug pane) should import this instead of re-deriving the string, mirroring
 * the rule the legacy Backbone board applied ad hoc in four different files
 * (fn.tools, sizer_popup.js, tab.ahu.js, tab.lifting.lug.js).
 */

export interface IAddressable {
  row?: number | null;
  level?: number | null;
  position?: number | null;
}

/** `"row:level"` for non-hybrid, `"row:level:position"` for hybrid. */
export function formatTunnelAddress(
  tunnel: IAddressable,
  isHybrid: boolean | undefined
): string {
  const row = tunnel.row ?? 1;
  const level = tunnel.level ?? 1;
  const position = tunnel.position ?? 1;
  return isHybrid ? `${row}:${level}:${position}` : `${row}:${level}`;
}

/** `"row:level"` only — used to key/group back-to-back peers together. */
export function formatRowLevel(tunnel: IAddressable): string {
  const row = tunnel.row ?? 1;
  const level = tunnel.level ?? 1;
  return `${row}:${level}`;
}

/** Stable sort key for row:level:position ordering (ties broken left to right). */
export function tunnelAddressRank(tunnel: IAddressable): [number, number, number] {
  return [tunnel.row ?? Infinity, tunnel.level ?? Infinity, tunnel.position ?? Infinity];
}

/**
 * `Array.sort` comparator for row:level:position order — the order the Data
 * Bar tunnel list and the tunnel-index pill strip both read in. Compared field
 * by field (never `Infinity - Infinity`, which is NaN and would make the sort
 * incoherent on a tunnel with no addressing).
 */
export function compareTunnelAddress(a: IAddressable, b: IAddressable): number {
  const ra = tunnelAddressRank(a);
  const rb = tunnelAddressRank(b);
  for (let i = 0; i < 3; i++) if (ra[i] !== rb[i]) return ra[i] - rb[i];
  return 0;
}

/**
 * getVisibleTunnelIndexes (fn_tools.js) — pagination window {start, end} of
 * at most `maxVisible` items, re-centered on `activeIndex`. Shared by every
 * tunnel-index pill strip (the canvas tab strip, the Lifting Lugs panel) so
 * they all window the same way.
 */
export function getVisibleTunnelIndexes(
  activeIndex: number,
  totalItems: number,
  maxVisible = 5
): { start: number; end: number } {
  if (totalItems <= maxVisible) {
    return { start: 0, end: totalItems };
  }
  const half = Math.floor(maxVisible / 2);
  let start = activeIndex - half;
  let end = start + maxVisible;
  if (start < 0) {
    start = 0;
    end = maxVisible;
  }
  if (end > totalItems) {
    end = totalItems;
    start = end - maxVisible;
  }
  return { start, end };
}
